import { ElementRef, Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import { ImageScalingDirectionEnum } from "../../../models/image-scaling-direction.enum";

const EMPTY_VALUE = 0;

@Injectable()
export class ScaledImageHelperService implements OnDestroy {

  private readonly hostAspectRatioBS = new BehaviorSubject<number>(EMPTY_VALUE);
  private readonly imageAspectRatioBS = new BehaviorSubject<number>(EMPTY_VALUE);
  private readonly scalingDirectionConfig = new BehaviorSubject<ImageScalingDirectionEnum>(ImageScalingDirectionEnum.AUTO);
  private resizeObserver: ResizeObserver|null = null;

  readonly scaleImageToWidth$ = combineLatest([
    this.scalingDirectionConfig.asObservable(),
    this.hostAspectRatioBS.asObservable(),
    this.imageAspectRatioBS.asObservable(),
  ]).pipe(
    map(([scalingDirection, hostAspectRatio, imageAspectRatio]) => {
      if (ImageScalingDirectionEnum.SCALE_TO_WIDTH === scalingDirection) {
        return true;
      }
      return this.isComparisonPossible(hostAspectRatio, imageAspectRatio) ? imageAspectRatio >= hostAspectRatio : false;
    })
  );

  readonly scaleImageToHeight$ = combineLatest([
    this.scalingDirectionConfig.asObservable(),
    this.hostAspectRatioBS.asObservable(),
    this.imageAspectRatioBS.asObservable(),
  ]).pipe(map(([scalingDirection, hostAspectRatio, imageAspectRatio]) => {
        if (ImageScalingDirectionEnum.SCALE_TO_WIDTH === scalingDirection) {
          return false;
        }
        return this.isComparisonPossible(hostAspectRatio, imageAspectRatio) ? imageAspectRatio <= hostAspectRatio : false;
      }
    )
  );

  constructor(private readonly zone: NgZone) {
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.hostAspectRatioBS.complete();
    this.imageAspectRatioBS.complete();
  }

  init(hostElement: ElementRef<HTMLElement>): void {
    if (this.resizeObserver) {
      throw new Error('Service already initialized.');
    }
    this.resizeObserver = new ResizeObserver(() =>
      this.zone.runOutsideAngular(() => {
        const clientRect = hostElement.nativeElement.getBoundingClientRect();
        const aspectRatio = +this.calculateAspectRatio(clientRect.width, clientRect.height).toFixed(4);
        if (aspectRatio !== this.hostAspectRatioBS.getValue()) {
          this.hostAspectRatioBS.next(aspectRatio);
        }
      })
    );
    this.resizeObserver.observe(hostElement.nativeElement);
  }

  setImage(img: HTMLImageElement|null): void {
    this.imageAspectRatioBS.next(this.calculateAspectRatio(img?.naturalWidth, img?.naturalHeight));
  }

  setScalingDirection(direction: ImageScalingDirectionEnum): void {
    this.scalingDirectionConfig.next(direction);
  }

  private calculateAspectRatio(width?: number|null, height?: number|null): number {
    if (!width || !height) {
      return EMPTY_VALUE;
    }
    const aspectRatio = width / height;
    return isNaN(aspectRatio) ? EMPTY_VALUE : aspectRatio;
  }

  private isComparisonPossible(hostAspectRatio: number, imageAspectRatio: number): boolean {
    return hostAspectRatio > EMPTY_VALUE && imageAspectRatio > EMPTY_VALUE;
  }
}
